<template>
  <div class="cp-index-toolbar">
    <div class="filters-wrapper">
      <AppSearchInput
        class="cp-index-toolbar__search mr-2"
        search-placeholder="Wyszukaj"
        @search="filterCommercialPrint({ query: $event })"
      />

      <v-autocomplete
        :value="selectedFilters.commercialPrintTypes"
        :items="filtersOptions.commercialPrintTypes"
        placeholder="Typ wniosku"
        v-bind="props.multiselect"
        class="mr-2 width-220 max-width-220"
        @input="selectToggleFilter({ commercialPrintTypes: $event })"
      >
        <template #selection="{ item, index }">
          <AppMultiselectChoices
            :index="index"
            :array-length="selectedFilters.commercialPrintTypes.length"
            :label="selectedFilters.commercialPrintTypes.length > 1 ? `${item.slice(0, 3)}...` : item"
          />
        </template>
      </v-autocomplete>

      <v-autocomplete
        :value="selectedFilters.commercialPrintMembers"
        :items="usersOptions"
        placeholder="Zespół projektowy"
        item-value="id"
        item-text="name"
        v-bind="props.multiselect"
        class="mr-2 width-220"
        @input="selectToggleFilter({ commercialPrintMembers: $event })"
      >
        <template #selection="{ item, index }">
          <AppMultiselectChoices
            :index="index"
            :array-length="selectedFilters.commercialPrintMembers.length"
            :label="item.name.slice(0, 3)"
          />
        </template>
      </v-autocomplete>

      <v-autocomplete
        :value="selectedFilters.commercialPrintOrderers"
        :items="usersOptions"
        placeholder="Zespół zlecający"
        item-value="id"
        item-text="name"
        v-bind="props.multiselect"
        class="mr-2 width-220"
        @input="selectToggleFilter({ commercialPrintOrderers: $event })"
      >
        <template #selection="{ item, index }">
          <AppMultiselectChoices
            :index="index"
            :array-length="selectedFilters.commercialPrintOrderers.length"
            :label="item.name.slice(0, 3)"
          />
        </template>
      </v-autocomplete>

      <v-autocomplete
        :value="selectedFilters.commercialPrintManagers"
        :items="usersOptions"
        placeholder="Zespół zarządzający"
        item-value="id"
        item-text="name"
        v-bind="props.multiselect"
        class="mr-2 width-220"
        @input="selectToggleFilter({ commercialPrintManagers: $event })"
      >
        <template #selection="{ item, index }">
          <AppMultiselectChoices
            :index="index"
            :array-length="selectedFilters.commercialPrintManagers.length"
            :label="item.name.slice(0, 3)"
          />
        </template>
      </v-autocomplete>

      <v-select
        v-if="departmentFilterPermitted"
        :value="selectedFilters.department"
        :items="filtersOptions.departments"
        item-value="id"
        item-text="name"
        placeholder="Oddział"
        v-bind="props.select"
        class="mr-2 width-220"
        @input="selectToggleFilter({ department: $event })"
      >
        <template #selection="{ item }">
          {{ item.shortcut }}
        </template>
      </v-select>

      <v-menu
        ref="datePicker"
        v-model="isDatePickerShown"
        :close-on-content-click="false"
        class="width-140"
        transition="scale-transition"
        offset-y
        z-index="11"
        max-width="290"
        min-width="290"
      >
        <template #activator="{ on, attrs }">
          <div v-on="on">
            <v-text-field
              :value="dateFormatted"
              persistent-hint
              v-bind="{ ...attrs, ...props.shortTextField }"
              readonly
              hide-details
              clearable
              color="primary"
              placeholder="Data"
              class="input-calendar mr-2 width-220"
              append-icon="mdi-calendar-month-outline"
              @click:clear="filterCommercialPrint({ daterange: [] })"
            />
          </div>
        </template>

        <v-date-picker
          v-model="date"
          v-bind="props.datePicker"
          range
          :title-date-format="() => dateFormatted"
          @change="filterCommercialPrint({ daterange: $event.sort() })"
          @input="isDatePickerShown = false"
        />
      </v-menu>
    </div>
    <div class="d-flex flex-column align-start">
      <v-btn
        depressed
        color="primary"
        class="mr-2"
        :to="{ name: 'create-commercial-print' }"
      >
        Nowy wniosek
      </v-btn>
      <v-btn
        v-if="filtersActive"
        text
        class="primary--text clear-filters-button mr-2"
        @click="clearAllFilters"
      >
        Wyczyść filtry
      </v-btn>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import props from '@/helpers/componentsProps';
import utils from '@/helpers/utils';
import AppSearchInput from '@/components/UI/AppSearchInput.vue';
import AppMultiselectChoices from '@/components/UI/AppMultiselectChoices.vue';

export default {
  components: {
    AppSearchInput,
    AppMultiselectChoices,
  },

  data() {
    return {
      isDatePickerShown: false,
      date: [],
      props,
    };
  },

  computed: {
    ...mapState('filters', ['filtersOptions', 'selectedFilters']),

    ...mapGetters('auth', ['checkUserPermissions','checkBelongsToDko']),

    filtersActive() {
      return utils.areFiltersSet(this.selectedFilters, true);
    },

    dateFormatted() {
      return utils.formatDateOutput(this.date);
    },

    usersOptions() {
      return this.filtersOptions.users.map((user) => ({
        ...user,
        name: user.firstName + ' ' + user.lastName,
      }));
    },

    departmentFilterPermitted() {
      return this.checkBelongsToDko;
    },
  },

  methods: {
    ...mapActions('commercialPrint', ['fetchCommercialPrint']),

    ...mapActions('filters', ['setFilters', 'clearFilters']),

    filterCommercialPrint(filter) {
      this.setFilters(filter).then(() => {
        this.fetchCommercialPrint();
      });
    },

    selectToggleFilter(filterObject) {
      utils.checkIfFilterExists(filterObject, this.selectedFilters, this.filterCommercialPrint);
    },

    clearAllFilters() {
      const { status } = this.selectedFilters;
      this.clearFilters({ status }).then(() => {
        this.fetchCommercialPrint();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.cp-index-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}
.filters-wrapper {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  flex-wrap: wrap;
  position: relative;
  max-width: 920px;

  &__search {
    width: 220px;
    max-width: 220px;
  }
}
</style>
